/**
 * Analytics event names and property types.
 * Used for PostHog (and Mixpanel via pipeline). Keep names stable for funnels.
 *
 * Naming: zporti_[category]_[action] — product prefix for Lexicon, avoids $system collisions.
 */

const PREFIX = 'zporti_'

export const ANALYTICS_EVENTS = {
  // Auth & onboarding
  SIGN_UP_STARTED: `${PREFIX}sign_up_started`,
  SIGN_UP_COMPLETED: `${PREFIX}sign_up_completed`,
  SIGN_IN_STARTED: `${PREFIX}sign_in_started`,
  SIGN_IN_COMPLETED: `${PREFIX}sign_in_completed`,
  TUTORIAL_STARTED: `${PREFIX}tutorial_started`,
  TUTORIAL_COMPLETED: `${PREFIX}tutorial_completed`,
  INVITE_LINK_VIEWED: `${PREFIX}invite_link_viewed`,

  // App shell
  SCREEN_VIEWED: `${PREFIX}screen_viewed`,

  // Home landing page (specs/home-page)
  HOME_VIEWED: `${PREFIX}home_viewed`,
  HOME_SLATE_MONKEY_TAPPED: `${PREFIX}home_slate_monkey_tapped`,
  HOME_SLATE_SEND_TAPPED: `${PREFIX}home_slate_send_tapped`,
  HOME_STREAK_TAPPED: `${PREFIX}home_streak_tapped`,

  // Create challenge
  CREATE_CHALLENGE_STARTED: `${PREFIX}create_challenge_started`,
  CREATE_CHALLENGE_STEP_VIEWED: `${PREFIX}create_challenge_step_viewed`,
  CREATE_CHALLENGE_STEP_COMPLETED: `${PREFIX}create_challenge_step_completed`,
  CREATE_CHALLENGE_STEP_BACK: `${PREFIX}create_challenge_step_back`,
  CREATE_CHALLENGE_PARTICIPANT_SELECTED: `${PREFIX}create_challenge_participant_selected`,
  CREATE_CHALLENGE_PICK_REMOVED: `${PREFIX}create_challenge_bet_removed`,
  CHALLENGE_CREATED: `${PREFIX}challenge_created`,
  CREATE_CHALLENGE_ABANDONED: `${PREFIX}create_challenge_abandoned`,

  MENU_OPENED: `${PREFIX}menu_opened`,
  MENU_ITEM_CLICKED: `${PREFIX}menu_item_clicked`,
  THEME_CHANGED: `${PREFIX}theme_changed`,
  AVATAR_PICKER_OPENED: `${PREFIX}avatar_picker_opened`,
  AVATAR_SELECTED: `${PREFIX}avatar_selected`,
  TAB_CLICKED: `${PREFIX}tab_clicked`,
  SIGN_OUT_CLICKED: `${PREFIX}sign_out_clicked`,
  COUPON_REDEEMED: `${PREFIX}coupon_redeemed`,

  // Friends & invite
  INVITE_MODAL_OPENED: `${PREFIX}invite_modal_opened`,
  INVITE_SENT: `${PREFIX}invite_sent`,
  FRIEND_ACCEPTED: `${PREFIX}friend_accepted`,
  FRIEND_SUGGESTION_ADD: `${PREFIX}friend_suggestion_add`,
  FRIEND_SUGGESTIONS_SHOWN: `${PREFIX}friend_suggestions_shown`,
  FRIEND_PROFILE_VIEWED: `${PREFIX}friend_profile_viewed`,

  // Groups
  CREATE_GROUP_STARTED: `${PREFIX}create_group_started`,
  GROUP_CREATED: `${PREFIX}group_created`,
  GROUP_JOINED: `${PREFIX}group_joined`,
  GROUP_INVITE_SENT: `${PREFIX}group_invite_sent`,

  // Challenges & predicting
  CHALLENGE_VIEWED: `${PREFIX}challenge_viewed`,
  CHALLENGE_LIST_VIEWED: `${PREFIX}challenge_list_viewed`,
  BET_PLACED: `${PREFIX}bet_placed`,
  GAME_RESULTS_VIEWED: `${PREFIX}game_results_viewed`,

  // Daily games (Warmup hub: Wordle, Higher or Lower, …)
  /** A user opened a specific daily game from the hub. Split by game_key. */
  GAME_STARTED: `${PREFIX}game_started`,
  /** A first-run how-to-play intro sheet was shown for a daily game. Split by game_key. */
  GAME_INTRO_SHOWN: `${PREFIX}game_intro_shown`,
  /** A daily game resolved (Wordle won/lost; Higher or Lower run ended). */
  GAME_COMPLETED: `${PREFIX}game_completed`,
  /** A level-based daily game (Wall Breaker) cleared a single level. Split by game_key; `level` = which. */
  GAME_LEVEL_CLEARED: `${PREFIX}game_level_cleared`,
  /** A level-based daily game level failed (out of balls / joker in goal / flag lost off pitch). */
  GAME_LEVEL_FAILED: `${PREFIX}game_level_failed`,
  /** A milestone level was reached (Wall Breaker: every 10th). `reason` = 'minor' | 'major' (quarters). */
  GAME_MILESTONE_REACHED: `${PREFIX}game_milestone_reached`,
  /** A user shared/copied a daily game result. */
  GAME_SHARED: `${PREFIX}game_shared`,
  /** A user opened the games leaderboard sheet. Split by game_key (the active tab). */
  LEADERBOARD_OPENED: `${PREFIX}leaderboard_opened`,

  // Guest mode
  GUEST_MODE_STARTED: `${PREFIX}guest_mode_started`,
  GUEST_SIGNUP_PROMPT_SHOWN: `${PREFIX}guest_signup_prompt_shown`,
  GUEST_SIGNUP_PROMPT_ACCEPTED: `${PREFIX}guest_signup_prompt_accepted`,
  GUEST_SIGNUP_PROMPT_DISMISSED: `${PREFIX}guest_signup_prompt_dismissed`,
  GUEST_CONVERTED: `${PREFIX}guest_converted`,
  GUEST_MONKEY_SLIP_GATE_SHOWN: `${PREFIX}guest_monkey_slip_gate_shown`,
  /** Guest Send → pending saved, navigated to Arena (no dialog on Create). */
  GUEST_MONKEY_SLIP_SAVED_TO_ARENA: `${PREFIX}guest_monkey_slip_saved_to_arena`,
  GUEST_MONKEY_ARENA_LOGIN_PROMPT_SHOWN: `${PREFIX}guest_monkey_arena_login_prompt_shown`,
  GUEST_MONKEY_ARENA_LOGIN_PROMPT_SIGNUP: `${PREFIX}guest_monkey_arena_login_prompt_signup`,
  GUEST_MONKEY_ARENA_LOGIN_PROMPT_LATER: `${PREFIX}guest_monkey_arena_login_prompt_later`,
  GUEST_MONKEY_SLIP_SIGNUP: `${PREFIX}guest_monkey_slip_signup`,
  GUEST_MONKEY_SLIP_DISMISSED: `${PREFIX}guest_monkey_slip_dismissed`,
  GUEST_MONKEY_CHALLENGE_FLUSHED: `${PREFIX}guest_monkey_challenge_flushed`,
  GUEST_MONKEY_CHALLENGE_FLUSH_FAILED: `${PREFIX}guest_monkey_challenge_flush_failed`,
  GUEST_MONKEY_CHALLENGE_FLUSH_SKIPPED_POST_KICKOFF: `${PREFIX}guest_monkey_challenge_flush_skipped_post_kickoff`,

  // Guest walkthrough
  GUEST_WALKTHROUGH_STARTED:      `${PREFIX}guest_walkthrough_started`,
  GUEST_WALKTHROUGH_STEP_VIEWED:  `${PREFIX}guest_walkthrough_step_viewed`,
  GUEST_WALKTHROUGH_COMPLETED:    `${PREFIX}guest_walkthrough_completed`,
  GUEST_WALKTHROUGH_DISMISSED:    `${PREFIX}guest_walkthrough_dismissed`,

  // Review-app prompt (specs/review-prompt) — one-time, post correct WC pick
  REVIEW_PROMPT_SHOWN: `${PREFIX}review_prompt_shown`,
  REVIEW_PROMPT_ACCEPTED: `${PREFIX}review_prompt_accepted`,
  REVIEW_PROMPT_DISMISSED: `${PREFIX}review_prompt_dismissed`,

  // Account deletion
  ACCOUNT_DELETION_STARTED: `${PREFIX}account_deletion_started`,
  ACCOUNT_DELETION_CANCELLED: `${PREFIX}account_deletion_cancelled`,
  ACCOUNT_DELETION_REQUESTED: `${PREFIX}account_deletion_requested`,
  ACCOUNT_DELETION_FAILED: `${PREFIX}account_deletion_failed`,

  // Landing
  LANDING_VIEWED: `${PREFIX}landing_viewed`,
  CONTACT_FORM_SUBMITTED: `${PREFIX}contact_form_submitted`,
  CTA_CLICKED: `${PREFIX}cta_clicked`,
} as const

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

/** Screen names for screen_viewed / screen() */
export const ANALYTICS_SCREENS = {
  HOME: 'home',
  FRIENDS: 'friends',
  FRIENDS_GROUP: 'friends_group',
  CREATE: 'create',
  CREATE_MATCH_DETAILS: 'create_match_details',
  CHALLENGE_DETAIL: 'challenge_detail',
  GAME_RESULTS: 'game_results',
  SIGN_IN: 'sign_in',
  SIGN_UP: 'sign_up',
  TUTORIAL: 'tutorial',
  INVITE: 'invite',
  FRIEND_PROFILE: 'friend_profile',
  LEAGUE_DETAIL: 'league_detail',
  LANDING: 'landing',
  PRIVACY_AND_DATA: 'privacy_and_data',
  LEGAL_DOCUMENT: 'legal_document',
  GAMES_HUB: 'games_hub',
} as const

export type AnalyticsScreenName = (typeof ANALYTICS_SCREENS)[keyof typeof ANALYTICS_SCREENS]

/** Optional properties for events (use only what you need) */
export interface AnalyticsEventProperties {
  walkthrough_step?: string
  /** Distinguishes auto/system-triggered events from user actions (e.g. walkthrough auto-end). */
  reason?: string
  source?: string
  // Rival-first create flow steps (specs/challenge-flow-redesign). `success` retained for the sent state.
  step?: 'rival' | 'build' | 'review' | 'success'
  from_step?: 'rival' | 'build' | 'review' | 'success'
  to_step?: 'rival' | 'build' | 'review' | 'success'
  challenge_id?: string
  participants_count?: number
  games_count?: number
  picks_count?: number
  /** Home streak card tap: whether today was still unclaimed when the Daily reward sheet opened. */
  can_claim?: boolean
  method?: 'manual' | 'link' | 'email' | 'oauth'
  count?: number
  group_id?: string
  members_count?: number
  group_members_count?: number
  invite_count?: number
  /** Recommended-friends ("People you may know") — the added candidate + its signals. */
  target_user_id?: string
  mutual_friend_count?: number
  shared_squad_org_count?: number
  match_id?: string
  game_id?: string
  path?: string
  screen?: AnalyticsScreenName | string
  location?: string
  text?: string
  token_preview?: string
  has_account?: boolean
  element?: string
  participant_type?: 'friend' | 'group'
  menu_item?:
    | 'tutorial'
    | 'world_cup_rules'
    | 'sign_out'
    | 'cancel'
    | 'privacy_and_data'
    | 'privacy_policy_modal'
    | 'privacy_data_deletion_modal'
    | 'privacy_terms_modal'
    | 'privacy_credits_modal'
    | 'privacy_account_deletion_hub'
    | 'privacy_data_controls'
    | 'privacy_data_export'
    | 'privacy_marketing_consent'
    | 'official_website'
    | 'redeem_coupon'
  theme_mode?: 'light' | 'dark'
  avatar_id?: string
  tab?: string
  slip_type?: 'match_winner' | 'other'
  /** The action that triggered a guest signup prompt (e.g. 'create challenges') */
  action?: string
  error_type?: string
  deletion_status?: string
  /** Always set: 'development' | 'production' — filter dev data in Mixpanel */
  environment?: 'development' | 'production'
  /** Coins granted by promo coupon redemption */
  awarded?: number
  /** Daily game identifier — 'WORDLE' | 'HILO' | 'FOOTBALLER' (server game keys). */
  game_key?: string
  /** Daily game outcome (Wordle solved, etc.). */
  won?: boolean
  /** Current/ending streak for a daily game. */
  streak?: number
  /** Wordle guesses used when the puzzle resolved. */
  guesses_used?: number
  /** Higher or Lower best run length. */
  best_run?: number
  /** Wall Breaker (slingshot) level number a clear/fail/complete event refers to. */
  level?: number
  /** Monkey Worlds sport-world index (0-3) a level event belongs to; `level` is the slot within it. */
  world?: number
  /** Monkey Worlds level clear time in 60 Hz ticks (the par-time input to the star rating). */
  time_ticks?: number
  /** Wall Breaker / Match Sport star rating (1-3) earned on a level clear. */
  stars?: number
  /** Match Sport in-level score when a level resolved (advisory — never an economy input). */
  score?: number
  /** Wall Breaker balls remaining when a level was cleared. */
  balls_left?: number
  /** Wall Breaker coins the server actually awarded for a level clear (0 if capped / in cooldown / offline). */
  coins_awarded?: number
  /** Daily game accuracy % (slider-style games). */
  accuracy?: number
  /** A player's best daily accuracy %. */
  best_accuracy?: number
  /** Striker Run — what ended the run: 'tackled' | 'saved' | 'off' | 'slow'. */
  cause?: string
  /** Keeper Duel — the ending save was within fingertip range of a goal. */
  near_miss?: boolean
  /** Striker Run distance covered in a run (meters). */
  distance?: number
  /** Striker Run cosmetic coins picked up in a run (nothing banks in v1). */
  coins_collected?: number
  /** Striker Run runs played in the session when the player exited. */
  runs_played?: number
  /** Blocky Striker points scored in a run (session best on `reason: 'exit'`). */
  points?: number
}
